import type { QueryClient } from '@tanstack/react-query';

import { REMEMBERED_DEVICE_ALERT_ID_LIMIT } from '../constants/device-alerts.js';
import type { Device } from '../types/device.js';
import type { DeviceAlert } from '../types/device-alert.js';

// The cache must never hold a device without an `alerts` array: a response from a zahrada that
// predates the field (or any other future producer that omits it) would otherwise crash the card.
// Every place a device from an HTTP response enters the `['devices', …]` query cache must run its
// `alerts` field through this helper first.
export const normalizeDeviceAlerts = (alerts: unknown): DeviceAlert[] =>
  Array.isArray(alerts) ? (alerts as DeviceAlert[]) : [];

/** `null` for an alert that never expires on its own, or whose expiry is unreadable. */
export const resolveDeviceAlertExpiryMs = (alert: DeviceAlert): number | null => {
  const expiryMs = alert.expiresAt == null ? Number.NaN : Date.parse(alert.expiresAt);
  return Number.isFinite(expiryMs) ? expiryMs : null;
};

// zahrada derives expiry on read and publishes nothing when an alert expires, so the client drops
// an expired alert itself; acking one would only return 409.
export const selectLiveDeviceAlerts = (alerts: DeviceAlert[], nowMs: number): DeviceAlert[] =>
  alerts.filter((alert) => {
    const expiryMs = resolveDeviceAlertExpiryMs(alert);
    return expiryMs === null || expiryMs > nowMs;
  });

// Both id sets only cover a snapshot or refetch that raced a recent ack or frame, so a long-lived tab
// keeps the newest ids and forgets the oldest instead of growing without bound.
const rememberRecentAlertId = (alertIds: Set<string>, alertId: string): void => {
  alertIds.add(alertId);
  for (const oldestAlertId of alertIds) {
    if (alertIds.size <= REMEMBERED_DEVICE_ALERT_ID_LIMIT) {
      return;
    }
    alertIds.delete(oldestAlertId);
  }
};

// zahrada never reuses an alert id once it leaves `active`, so an acknowledged id is never new again,
// even in a snapshot that zahrada read before the acknowledge committed.
const acknowledgedAlertIds = new Set<string>();

export const rememberAcknowledgedDeviceAlert = (alertId: string): void => {
  rememberRecentAlertId(acknowledgedAlertIds, alertId);
};

// A frame can announce an alert while a reconnect refetch is in flight, and that refetch compares
// against a snapshot taken before the frame, so it would announce the alert again.
const announcedAlertIds = new Set<string>();

export const rememberAnnouncedDeviceAlerts = (alertIds: readonly string[]): void => {
  alertIds.forEach((alertId) => rememberRecentAlertId(announcedAlertIds, alertId));
};

type DevicesPage = { devices: Device[]; total: number };

const readCachedDevices = (queryClient: QueryClient): Device[] =>
  queryClient
    .getQueriesData<DevicesPage>({ queryKey: ['devices'] })
    .flatMap(([, devicesPage]) => devicesPage?.devices ?? []);

const selectUnseenAlertIds = (alerts: DeviceAlert[], seenAlertIds: ReadonlySet<string>): string[] =>
  alerts
    .filter(
      (alert) =>
        !seenAlertIds.has(alert.id) &&
        !acknowledgedAlertIds.has(alert.id) &&
        !announcedAlertIds.has(alert.id),
    )
    .map((alert) => alert.id);

/** The alert ids each cached device holds, merged across every cached `['devices', …]` page. */
export const snapshotCachedDeviceAlertIds = (
  queryClient: QueryClient,
): Map<string, Set<string>> => {
  const alertIdsByDeviceId = new Map<string, Set<string>>();
  for (const cachedDevice of readCachedDevices(queryClient)) {
    const alertIds = alertIdsByDeviceId.get(cachedDevice.id) ?? new Set<string>();
    for (const alert of normalizeDeviceAlerts(cachedDevice.alerts)) {
      alertIds.add(alert.id);
    }
    alertIdsByDeviceId.set(cachedDevice.id, alertIds);
  }
  return alertIdsByDeviceId;
};

// Only a device held in a cached `['devices', …]` page can report a new alert: on a page that does
// not list devices, alerts stay silent once that query has been garbage-collected.
export const selectNewDeviceAlertIds = (
  queryClient: QueryClient,
  deviceId: string,
  incomingAlerts: DeviceAlert[],
): string[] => {
  const cachedAlertIds = snapshotCachedDeviceAlertIds(queryClient).get(deviceId);
  return cachedAlertIds ? selectUnseenAlertIds(incomingAlerts, cachedAlertIds) : [];
};

// A device missing from the snapshot entered the cache with a page or filter change, so its alerts
// were not raised while this client was watching it.
export const selectDeviceAlertIdsNewSinceSnapshot = (
  queryClient: QueryClient,
  snapshot: ReadonlyMap<string, ReadonlySet<string>>,
): string[] => {
  const newAlertIds = new Set<string>();
  for (const cachedDevice of readCachedDevices(queryClient)) {
    const snapshotAlertIds = snapshot.get(cachedDevice.id);
    if (!snapshotAlertIds) {
      continue;
    }
    selectUnseenAlertIds(normalizeDeviceAlerts(cachedDevice.alerts), snapshotAlertIds).forEach(
      (alertId) => newAlertIds.add(alertId),
    );
  }
  return [...newAlertIds];
};
